import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export default function LoginPage({ portalType = 'admin' }) {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(location.state?.googleError || '');
  const [loading, setLoading] = useState(false);

  // Already signed in (e.g. swiped back to this page) — bounce straight into the dashboard
  useEffect(() => {
    if (user) navigate(user.role === 'admin' ? '/admin' : '/vendor', { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(email, password);
      if (portalType === 'admin' && user.role !== 'admin') {
        setError('Admin access required');
        return;
      }
      if (portalType === 'vendor' && user.role !== 'vendor') {
        setError('Vendor access required');
        return;
      }
      navigate(user.role === 'admin' ? '/admin' : '/vendor');
    } catch (err) {
      setError(err.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const isVendor = portalType === 'vendor';

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col" data-testid="login-page">
      {/* Top visual area */}
      {isVendor ? (
        <div className="relative flex-1 min-h-[35vh] flex items-end overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-transparent to-[#0a0a0a]" />
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[300px] h-[300px] rounded-full bg-[#D4A843] blur-[120px]" />
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 px-8 pb-8 w-full"
          >
            <p className="text-[#D4A843] text-sm font-medium tracking-[0.2em] uppercase mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Vendor Portal</p>
            <h1 className="text-white text-4xl font-medium tracking-tight leading-[1.1]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Exotic Animals<br />Miami
            </h1>
          </motion.div>
        </div>
      ) : (
        <div className="flex-1 min-h-[25vh] flex items-end px-8 pb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-[#D4A843] text-sm font-medium tracking-[0.2em] uppercase mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Admin Portal</p>
            <h1 className="text-white text-4xl font-medium tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>Dashboard</h1>
          </motion.div>
        </div>
      )}

      {/* Form area */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className="px-8 pb-12"
      >
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="text-[#EF4444] text-sm py-3 px-4 bg-[#EF4444]/10 rounded-2xl"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="login-error"
            >
              {error}
            </motion.div>
          )}
          <div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-transparent border-b border-white/15 px-0 py-4 text-white text-lg focus:border-[#D4A843] focus:outline-none transition-colors placeholder:text-neutral-600"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              placeholder="Email"
              required
              data-testid="login-email"
            />
          </div>
          <div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent border-b border-white/15 px-0 py-4 text-white text-lg focus:border-[#D4A843] focus:outline-none transition-colors placeholder:text-neutral-600"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              placeholder="Password"
              required
              data-testid="login-password"
            />
          </div>
          <div className="pt-4">
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#D4A843] text-black font-semibold py-4 rounded-full text-base tracking-wide shadow-[0_0_25px_rgba(212,168,67,0.25)] hover:bg-[#E5B954] active:scale-[0.98] transition-all disabled:opacity-50"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="login-submit"
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </button>
          </div>
        </form>

        {/* Divider */}
        <div className="flex items-center gap-4 my-6">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-neutral-600 text-xs uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>or</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        {/* Google Login */}
        <button
          onClick={handleGoogleLogin}
          className="w-full flex items-center justify-center gap-3 bg-white/5 border border-white/10 text-white font-medium py-4 rounded-full text-base tracking-wide hover:bg-white/10 active:scale-[0.98] transition-all"
          style={{ fontFamily: 'Manrope, sans-serif' }}
          data-testid="google-login-btn"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          Continue with Google
        </button>

        <div className="text-center mt-8">
          <a href="/" className="text-neutral-600 text-sm hover:text-neutral-400 transition-colors" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Back to website
          </a>
          {isVendor && (
            <span className="block mt-3">
              <a href="/vendor/apply" className="text-[#D4A843]/60 text-sm hover:text-[#D4A843] transition-colors" style={{ fontFamily: 'Manrope, sans-serif' }}>
                New vendor? Apply here
              </a>
            </span>
          )}
        </div>
      </motion.div>
    </div>
  );
}
